/**
 * 파이프라인 빌더 공유 타입 정의
 *
 * PipelineBuilderModal과 각 Step 컴포넌트들이 공통으로 사용하는 타입·상수
 */

import { ChunkingConfig, PreprocessingConfig } from '../../../api';

// ── 소스 타입 ──
export type SourceType = 'file' | 'web' | 'database';

// ── 파서 타입 (AWS Bedrock 파싱 옵션 대응) ──
export type ParserType = 'default' | 'foundation-model';

// ── 파서 설정 ──
export interface ParserConfig {
  parser: ParserType;
  /** foundation-model 파서일 때 사용할 Vision 모델 키 (예: 'nova-lite') */
  fmModelId?: string;
  /** foundation-model 파서일 때 커스텀 파싱 프롬프트 (선택) */
  parsingPrompt?: string;
}

/** 파서 메타 정보 (UI 표시용) */
export interface ParserOptionMeta {
  value: ParserType;
  title: string;
  desc: string;
  supportedFiles: string;
  pricing: string;
  /** 실제 백엔드 구현 여부 — false면 "준비 중"으로 표시하고 선택 불가 */
  implemented: boolean;
}

export const PARSER_OPTIONS: Record<ParserType, ParserOptionMeta> = {
  'default': {
    value: 'default',
    title: '기본 파서',
    desc: '문서에서 텍스트를 추출합니다. 이미지 형태의 PDF(스캔 문서)는 AI OCR로 자동 처리됩니다. TXT·Excel에 적합합니다.',
    supportedFiles: 'TXT · MD · HTML · DOCX · Excel · PDF(AI OCR 자동)',
    pricing: '무료',
    implemented: true,
  },
  'foundation-model': {
    value: 'foundation-model',
    title: 'AI 파서 (FM)',
    desc: 'Vision 모델로 문서를 파싱하며 커스텀 프롬프트를 지원합니다. 한국어 문서에 유리합니다.',
    supportedFiles: 'PDF · 이미지',
    pricing: '토큰당 과금',
    implemented: true,
  },
};

// ── FM 파서에서 선택 가능한 Vision 모델 ──
export interface FmModelOption {
  id: string;      // 프론트 모델 키 (백엔드에서 Bedrock 모델 ID로 변환)
  name: string;
  provider: string;
  note: string;
  /** 100만 토큰 기준 입력/출력 비용 (USD) */
  inputCost: number;
  outputCost: number;
}

export const FM_PARSER_MODELS: FmModelOption[] = [
  { id: 'nova-lite',       name: 'Nova Lite',       provider: 'Amazon',    note: '저렴 · 빠름 · 한국어 양호', inputCost: 0.06, outputCost: 0.24 },
  { id: 'nova-pro',        name: 'Nova Pro',        provider: 'Amazon',    note: '정확 · 한국어 우수',        inputCost: 0.80, outputCost: 3.20 },
  { id: 'claude-3-haiku',  name: 'Claude 3 Haiku',  provider: 'Anthropic', note: '저렴 · 안정적',             inputCost: 0.25, outputCost: 1.25 },
  { id: 'claude-sonnet-4', name: 'Claude Sonnet 4', provider: 'Anthropic', note: '최고 정확도 · 비쌈',        inputCost: 3.00, outputCost: 15.00 },
];

export const DEFAULT_PARSER_CONFIG: ParserConfig = {
  parser: 'default',
};

/**
 * 업로드된 파일 확장자 집합으로부터 노출할 파서 목록을 결정한다.
 * - TXT/MD/Excel: 기본 파서만
 * - PDF: 기본 + BDA + FM
 * - 이미지(JPEG/PNG): BDA + FM
 * - 혼합: 합집합
 */
export function getAvailableParsers(files: File[]): ParserType[] {
  if (files.length === 0) return ['default'];

  const exts = new Set(
    files.map((f) => f.name.split('.').pop()?.toLowerCase() ?? ''),
  );

  const hasTextLike = ['txt', 'md', 'xlsx', 'xls', 'doc', 'docx', 'html', 'csv'].some((e) => exts.has(e));
  const hasPdf = exts.has('pdf');
  const hasImage = ['jpg', 'jpeg', 'png'].some((e) => exts.has(e));

  const available = new Set<ParserType>();

  if (hasTextLike || hasPdf) available.add('default');
  if (hasPdf || hasImage) available.add('foundation-model');
  // 이미지만 있는 경우 default 미포함 (이미지 텍스트 추출 불가)

  // 안전장치: 아무것도 없으면 기본 파서
  if (available.size === 0) available.add('default');

  // 표시 순서 고정
  return (['default', 'foundation-model'] as ParserType[])
    .filter((p) => available.has(p));
}

// ── 웹 설정 ──
export interface WebConfig {
  url: string;
  depth: number;
  includePatterns: string;
  excludePatterns: string;
  schedule: 'manual' | 'daily' | 'weekly';
  pageType: 'query' | 'path';
  pageParam: string;
  startPage: number;
  endPage: number;
}

// ── DB 설정 ──

/** 컬럼 한글 설명 (LLM 자동생성 or 사용자 편집) */
export interface DbColumnMeta {
  name: string;         // 컬럼명 (영문)
  type: string;         // 데이터 타입
  nullable: boolean;    // NULL 허용 여부
  comment: string;      // DB 코멘트 (없으면 '')
  label: string;        // 한글 설명 (LLM 생성 or 사용자 편집)
  include: boolean;     // KB에 포함할지 여부
}

/** 테이블 선택 정보 */
export interface DbTableSelection {
  schema: string;       // 스키마명
  table: string;        // 테이블명
  selected: boolean;    // 선택 여부
  columns: DbColumnMeta[];
  labelGenerated: boolean; // LLM 한글 설명 생성 완료 여부
}

/** SQL 힌트 항목 (설명 + 쿼리) */
export interface SqlHint {
  /** 어떤 질문에 쓰는 쿼리인지 설명 — 벡터 검색 시 이 텍스트로 매칭됨 */
  description: string;
  /** 실제 SQL 쿼리 */
  sql: string;
}

/** SQL 생성 기본 시스템 프롬프트 */
export const DEFAULT_SQL_SYSTEM_PROMPT =
`당신은 데이터베이스 전문가입니다. 사용자의 자연어 질문을 SQL 쿼리로 변환하세요.

## 규칙
1. 반드시 실행 가능한 SQL만 출력하세요. 설명이나 마크다운 코드블록 없이 순수 SQL만 출력합니다.
2. 스키마 문서에 나온 테이블명·컬럼명을 정확히 사용하세요.
3. SELECT 쿼리만 생성하세요. INSERT·UPDATE·DELETE·DROP은 절대 생성하지 마세요.
4. 결과가 너무 많을 수 있으니 LIMIT 100을 기본으로 사용하세요. 사용자가 명시하면 그에 따르세요.
5. 컬럼명에 공백이나 특수문자가 있으면 적절히 이스케이프하세요.
6. 질문을 SQL로 변환할 수 없으면 "CANNOT_GENERATE_SQL: (이유)"라고 출력하세요.
7. AS alias에 공백이 포함되면 반드시 백틱(\`)으로 감싸세요. 예: AS \`올해 매출합계\`
8. "차트", "그래프", "시각화" 표현이 있어도 데이터를 조회하는 SELECT SQL을 생성하세요.

## 날짜 조건 처리
- 이번주: YEARWEEK(날짜컬럼, 1) = YEARWEEK(NOW(), 1)
- 이번달: DATE_FORMAT(날짜컬럼, '%Y-%m') = DATE_FORMAT(NOW(), '%Y-%m')
- 오늘: DATE(날짜컬럼) = CURDATE()
- 날짜 컬럼명은 스키마에 맞게 대체하세요. (예: created_at, 등록일시, 생성일 등)`;

/** 결과 요약 기본 시스템 프롬프트 */
export const DEFAULT_SQL_SUMMARY_PROMPT =
`당신은 데이터 분석 어시스턴트입니다. SQL 실행 결과를 사용자가 이해하기 쉽게 한국어로 요약하세요.

## 규칙
1. 결과 데이터를 표나 목록으로 보기 좋게 정리하세요.
2. 핵심 내용을 먼저 요약하고, 세부 데이터를 아래에 나열하세요.
3. 데이터가 없으면 "조건에 맞는 데이터가 없습니다."라고 안내하세요.
4. 숫자·금액은 읽기 쉽게 포맷하세요 (예: 1,234,567).
5. 마크다운 표를 사용하면 더 좋습니다.
6. 사용자가 "차트", "그래프"를 요청했다면 데이터를 표로 제공하고 차트는 응답 아래에 자동으로 표시됩니다.

## 출력 형식 자동 판단
질문의 의도를 파악해서 가장 적합한 형식으로 응답하세요:
- 집계/통계 (합계, 건수, 평균 등) → 핵심 숫자 요약 + 마크다운 표
- 목록/조회 (목록, 현황, 리스트 등) → 마크다운 표
- 차트/그래프 요청 → 마크다운 표 + "차트는 아래에 자동 표시됩니다" 안내
- 단순 단답 (몇 개?, 누가?, 얼마?) → 핵심 값을 굵게 강조 후 간단 설명
- 이메일 발송용 → HTML 표 형식으로 정리`;

export interface DbConfig {
  type: 'mysql' | 'postgresql' | 'redshift' | 'oracle' | 'mssql';
  host: string;
  port: number;
  database: string;
  schema: string;
  username: string;
  password: string;
  tables: DbTableSelection[];
  syncSchedule: 'manual' | 'daily' | 'weekly';
  /** DB 커넥터는 Text-to-SQL 단일 모드로 고정 (하위 호환용으로 필드 유지) */
  mode: 'text2sql';
  /** 샘플 데이터 행 수 (LLM이 데이터 형식 파악용, 0이면 포함 안함) */
  sampleRows: number;
  /** SQL 힌트 목록 — 각 항목이 별도 문서로 임베딩됨 */
  hints: SqlHint[];
  /** SQL 생성 시스템 프롬프트 (비워두면 기본값 사용) */
  sqlSystemPrompt?: string;
  /** 결과 요약 시스템 프롬프트 (비워두면 기본값 사용) */
  sqlSummaryPrompt?: string;
}

// ── 기본값 ──
export const DEFAULT_PREPROCESSING: PreprocessingConfig = {
  enableOcr: true,
  removeHeaders: true,
  removeFooters: true,
  extractTables: true,
  cleanWhitespace: true,
  dbNullHandling: false,
  dbDateFormat: false,
  dbNumberFormat: false,
  dbDeduplication: false,
  dbTextColumnsOnly: false,
  dbMaskSensitive: false,
};

export const DEFAULT_WEB_PREPROCESSING: PreprocessingConfig = {
  enableOcr: false,
  removeHeaders: false,
  removeFooters: false,
  extractTables: false,
  cleanWhitespace: true,
  // 웹 크롤링 표준 전처리 기본값
  removeBoilerplate: true,
  mainContentOnly: true,
  removeAds: false,
  dbNullHandling: false,
  dbDateFormat: false,
  dbNumberFormat: false,
  dbDeduplication: false,
  dbTextColumnsOnly: false,
  dbMaskSensitive: false,
};

export const DEFAULT_DB_PREPROCESSING: PreprocessingConfig = {
  enableOcr: false,
  removeHeaders: false,
  removeFooters: false,
  extractTables: true,
  cleanWhitespace: true,
  // DB 전용 기본값
  dbNullHandling: true,
  dbDateFormat: true,
  dbNumberFormat: false,
  dbDeduplication: false,
  dbTextColumnsOnly: false,
  dbMaskSensitive: false,
};

export const DEFAULT_CHUNKING: ChunkingConfig = {
  strategy: 'default',
  chunkSize: 300,        // AWS 권장 기본값 (토큰)
  chunkOverlap: 60,      // 300의 20%
  respectSentenceBoundary: true,
  parentChunkSize: 1500,
  childChunkSize: 300,
};

/** 웹 소스 기본 청킹 (웹 표준: 500토큰) */
export const DEFAULT_WEB_CHUNKING: ChunkingConfig = {
  strategy: 'default',
  chunkSize: 500,        // 웹 표준 (토큰)
  chunkOverlap: 100,     // 500의 20%
  respectSentenceBoundary: true,
  parentChunkSize: 1500,
  childChunkSize: 300,
};

export const DEFAULT_WEB_CONFIG: WebConfig = {
  url: '',
  depth: 2,
  includePatterns: '',
  excludePatterns: '',
  schedule: 'manual',
  pageType: 'query',
  pageParam: 'page',
  startPage: 1,
  endPage: 1,
};

export const DEFAULT_DB_CONFIG: DbConfig = {
  type: 'mysql',
  host: '',
  port: 3306,
  database: '',
  schema: '',
  username: '',
  password: '',
  tables: [],
  syncSchedule: 'manual',
  mode: 'text2sql',
  sampleRows: 3,
  hints: [],
  sqlSystemPrompt: DEFAULT_SQL_SYSTEM_PROMPT,
  sqlSummaryPrompt: DEFAULT_SQL_SUMMARY_PROMPT,
};

// ── 소스 레이블·아이콘 ──
export const SOURCE_LABELS: Record<SourceType, string> = {
  file: '파일',
  web: '웹',
  database: 'DB',
};

// ── 소스별 청킹 전략 옵션 ──
export const CHUNK_STRATEGIES: Record<
  SourceType,
  Array<{ value: ChunkingConfig['strategy']; title: string; desc: string }>
> = {
  file: [
    { value: 'default',      title: '기본값',     desc: 'AWS 권장 설정(약 300토큰, 문장 경계 존중)으로 자동 처리합니다. 대부분의 문서에 적합합니다.' },
    { value: 'fixed',        title: '고정 크기',  desc: '청크 크기·오버랩을 직접 지정해 분할합니다. 엑셀·표형 데이터에 적합합니다.' },
    { value: 'hierarchical', title: '계층형',     desc: '부모·자식 청크로 나눠 검색 정확도와 문맥을 함께 확보합니다. 긴 문서에 좋습니다.' },
    { value: 'semantic',     title: '의미 기반',  desc: 'AI가 의미 단위로 분할합니다. 가장 정확하지만 느리고 비용이 발생합니다.' },
    { value: 'none',         title: '청킹 안 함', desc: '문서 전체를 하나의 청크로 처리합니다. 이미 짧게 나뉜 문서에 적합합니다.' },
  ],
  web: [
    { value: 'default',   title: '기본값',      desc: '웹 표준 설정(약 500토큰, 오버랩)으로 페이지를 분할합니다. 대부분의 사이트에 적합합니다.' },
    { value: 'fixed',     title: '고정 크기',   desc: '청크 크기·오버랩을 직접 지정해 분할합니다.' },
    { value: 'paragraph', title: '문단 단위',   desc: '문단(빈 줄) 경계를 기준으로 분할합니다. 본문이 문단으로 잘 나뉜 페이지에 적합합니다.' },
  ],
  database: [
    { value: 'paragraph', title: '필드 조합',   desc: '여러 컬럼을 조합하여 청크를 생성합니다.' },
    { value: 'fixed',     title: '고정 길이',   desc: '긴 텍스트 필드를 일정한 토큰 수로 분할합니다.' },
  ],
};


